/**
 * Quality.jsx - Quality / NCR (Non-Conformance Report) Page
 *
 * Displays and manages Non-Conformance Reports for vendor quality tracking.
 *
 * Features:
 *   - NCR count display (KPI tiles)
 *   - Raise NCR button, Export button
 *   - DataTable with NCR columns
 *   - Click row opens NCR detail modal
 *   - Raise NCR modal with full form
 *   - Uses getNCRs() from '../api/quality'
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Plus,
  Download,
  FileText,
  ShieldAlert,
  CheckCircle2,
  Clock,
  XCircle,
} from "lucide-react";
import { getNCRs } from "../api/quality";
import { useCompany } from "../company/CompanyContext";
import DataTable from "../components/DataTable";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import Loading from "../components/Loading";
import Tile from "../components/Tile";
import { useToast } from "../components/Toast";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const NCR_TYPES = [
  "Quality",
  "Documentation",
  "Packaging",
  "Short Ship",
  "Late",
  "Service",
];

const SEVERITY_OPTIONS = ["Minor", "Major", "Critical"];

const STATUS_OPTIONS = ["Open", "In Progress", "Closed"];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function Quality() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedCompany } = useCompany();
  const companyFilter = selectedCompany?.id !== "all" ? selectedCompany?.name : undefined;

  /* Data state */
  const [ncrs, setNCRs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* Modals */
  const [showNCRDetail, setShowNCRDetail] = useState(false);
  const [selectedNCR, setSelectedNCR] = useState(null);
  const [showRaiseNCR, setShowRaiseNCR] = useState(false);

  /* Raise NCR form */
  const [ncrForm, setNcrForm] = useState({
    vendor_id: "",
    product_id: "",
    po_no: "",
    ncr_date: new Date().toISOString().split("T")[0],
    ncr_type: "Quality",
    severity: "Minor",
    qty_affected: 0,
    cost_impact: 0,
    status: "Open",
    closed_date: "",
    description: "",
    corrective_action: "",
  });

  /* ------------------------------------------------------------------ */
  /* Load NCRs on mount                                                  */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    async function loadNCRs() {
      try {
        setLoading(true);
        setError(null);
        const data = await getNCRs({ company: companyFilter });
        setNCRs(data);
      } catch (err) {
        console.error("Failed to load NCRs:", err);
        setError(err.message || "Failed to load NCRs");
        toast("Failed to load NCRs", "bad");
      } finally {
        setLoading(false);
      }
    }
    loadNCRs();
  }, [toast, selectedCompany]);

  /* ------------------------------------------------------------------ */
  /* KPI calculations                                                    */
  /* ------------------------------------------------------------------ */

  const totalNCRs = ncrs.length;
  const openNCRs = ncrs.filter((n) => n.status === "Open").length;
  const inProgressNCRs = ncrs.filter((n) => n.status === "In Progress").length;
  const closedNCRs = ncrs.filter((n) => n.status === "Closed").length;
  const criticalNCRs = ncrs.filter((n) => n.severity === "Critical").length;

  /* ------------------------------------------------------------------ */
  /* Export handler                                                       */
  /* ------------------------------------------------------------------ */

  const handleExport = useCallback(() => {
    try {
      // Simple CSV export
      const headers = [
        "NCR",
        "Date",
        "Vendor No",
        "Vendor",
        "Item",
        "PO",
        "Type",
        "Severity",
        "Qty",
        "Cost",
        "Description",
        "Status",
        "Closed Date",
      ];

      const rows = ncrs.map((n) => [
        n.ncr_no || n.id,
        n.ncr_date || n.date,
        n.vendor_no || n.vendor_id,
        n.vendor_name || n.vendor,
        n.item || n.product_id,
        n.po_no,
        n.ncr_type || n.type,
        n.severity,
        n.qty_affected || n.qty,
        n.cost_impact || n.cost,
        `"${(n.description || "").replace(/"/g, '""')}"`,
        n.status,
        n.closed_date || "",
      ]);

      const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ncr-report-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      toast("NCR report exported", "good");
    } catch (err) {
      console.error("Export failed:", err);
      toast("Export failed", "bad");
    }
  }, [ncrs, toast]);

  /* ------------------------------------------------------------------ */
  /* Row click handler                                                   */
  /* ------------------------------------------------------------------ */

  const handleRowClick = useCallback((row) => {
    setSelectedNCR(row);
    setShowNCRDetail(true);
  }, []);

  /* ------------------------------------------------------------------ */
  /* Raise NCR handler                                                   */
  /* ------------------------------------------------------------------ */

  const handleRaiseNCR = useCallback(() => {
    if (!ncrForm.vendor_id) {
      toast("Please select a vendor", "warn");
      return;
    }
    if (!ncrForm.product_id) {
      toast("Please select a product", "warn");
      return;
    }
    if (!ncrForm.description.trim()) {
      toast("Please provide a description", "warn");
      return;
    }

    try {
      // Save to localStorage (in production, this would call an API)
      const newNCR = {
        ...ncrForm,
        id: `NCR-${Date.now()}`,
        ncr_no: `NCR-${Date.now()}`,
        vendor_no: ncrForm.vendor_id,
        product_id: ncrForm.product_id,
        qty: ncrForm.qty_affected,
        cost: ncrForm.cost_impact,
        type: ncrForm.ncr_type,
        date: ncrForm.ncr_date,
      };

      const existingNCRs = JSON.parse(localStorage.getItem("vms-ncrs") || "[]");
      existingNCRs.push(newNCR);
      localStorage.setItem("vms-ncrs", JSON.stringify(existingNCRs));

      setNCRs((prev) => [...prev, newNCR]);
      toast("NCR raised successfully", "good");
      setShowRaiseNCR(false);

      // Reset form
      setNcrForm({
        vendor_id: "",
        product_id: "",
        po_no: "",
        ncr_date: new Date().toISOString().split("T")[0],
        ncr_type: "Quality",
        severity: "Minor",
        qty_affected: 0,
        cost_impact: 0,
        status: "Open",
        closed_date: "",
        description: "",
        corrective_action: "",
      });
    } catch (err) {
      console.error("Failed to raise NCR:", err);
      toast("Failed to raise NCR", "bad");
    }
  }, [ncrForm, toast]);

  /* ------------------------------------------------------------------ */
  /* Table columns                                                       */
  /* ------------------------------------------------------------------ */

  const columns = [
    { key: "ncr_no", label: "NCR", type: "text" },
    { key: "ncr_date", label: "Date", type: "date" },
    { key: "vendor_no", label: "Vendor No.", type: "text" },
    { key: "vendor_name", label: "Vendor", type: "text" },
    { key: "item", label: "Item", type: "text" },
    { key: "po_no", label: "PO", type: "text" },
    { key: "ncr_type", label: "Type", type: "text" },
    {
      key: "severity",
      label: "Severity",
      type: "badge",
      render: (value) => {
        const variant =
          value === "Critical" ? "bad" : value === "Major" ? "warn" : "info";
        return <Badge text={value} variant={variant} />;
      },
    },
    { key: "qty_affected", label: "Qty", type: "int" },
    { key: "cost_impact", label: "Cost", type: "money" },
    { key: "description", label: "Description", type: "text" },
    {
      key: "status",
      label: "Status",
      type: "badge",
      render: (value) => {
        const variant =
          value === "Closed" ? "good" : value === "In Progress" ? "warn" : "info";
        return <Badge text={value} variant={variant} />;
      },
    },
    { key: "closed_date", label: "Closed Date", type: "date" },
  ];

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */

  return (
    <div>
      <Loading show={loading} text="Loading NCRs..." />

      {/* KPI Tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
        <Tile label="Total NCRs" value={totalNCRs} />
        <Tile label="Open" value={openNCRs} variant={openNCRs > 0 ? "warn" : undefined} />
        <Tile label="In Progress" value={inProgressNCRs} variant={inProgressNCRs > 0 ? "warn" : undefined} />
        <Tile label="Closed" value={closedNCRs} variant="good" />
        <Tile label="Critical" value={criticalNCRs} variant={criticalNCRs > 0 ? "bad" : undefined} />
      </div>

      {/* Actions */}
      <div className="card">
        <div className="card-head">
          <h2>Non-Conformance Reports</h2>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" onClick={handleExport}>
              <Download size={14} />
              Export
            </button>
            <button className="btn primary" onClick={() => setShowRaiseNCR(true)}>
              <Plus size={14} />
              Raise NCR
            </button>
          </div>
        </div>
        <div className="card-body flush">
          {error ? (
            <div style={{ padding: 34, textAlign: "center", color: "var(--bad)" }}>
              <AlertTriangle size={24} style={{ marginBottom: 8 }} />
              <p style={{ fontWeight: 600 }}>{error}</p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              rows={ncrs}
              onRow={handleRowClick}
              emptyText="No NCRs found"
            />
          )}
        </div>
      </div>

      {/* NCR Detail Modal */}
      {showNCRDetail && selectedNCR && (
        <Modal
          title={`NCR: ${selectedNCR.ncr_no || selectedNCR.id}`}
          onClose={() => setShowNCRDetail(false)}
          footer={
            <button className="btn" onClick={() => setShowNCRDetail(false)}>
              Close
            </button>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, marginBottom: 4 }}>
                Date
              </div>
              <div style={{ fontSize: 13 }}>
                {selectedNCR.ncr_date
                  ? new Date(selectedNCR.ncr_date).toLocaleDateString("en-PH", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "—"}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, marginBottom: 4 }}>
                Status
              </div>
              <Badge
                text={selectedNCR.status}
                variant={
                  selectedNCR.status === "Closed"
                    ? "good"
                    : selectedNCR.status === "In Progress"
                    ? "warn"
                    : "info"
                }
              />
            </div>

            <div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, marginBottom: 4 }}>
                Vendor
              </div>
              <div style={{ fontSize: 13 }}>
                {selectedNCR.vendor_name || selectedNCR.vendor_no || selectedNCR.vendor_id || "—"}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, marginBottom: 4 }}>
                Item
              </div>
              <div style={{ fontSize: 13 }}>
                {selectedNCR.item || selectedNCR.product_id || "—"}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, marginBottom: 4 }}>
                Type
              </div>
              <div style={{ fontSize: 13 }}>{selectedNCR.ncr_type || selectedNCR.type || "—"}</div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, marginBottom: 4 }}>
                Severity
              </div>
              <Badge
                text={selectedNCR.severity}
                variant={
                  selectedNCR.severity === "Critical"
                    ? "bad"
                    : selectedNCR.severity === "Major"
                    ? "warn"
                    : "info"
                }
              />
            </div>

            <div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, marginBottom: 4 }}>
                Quantity Affected
              </div>
              <div style={{ fontSize: 13 }}>{selectedNCR.qty_affected || selectedNCR.qty || "—"}</div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, marginBottom: 4 }}>
                Cost Impact
              </div>
              <div style={{ fontSize: 13 }}>
                {selectedNCR.cost_impact || selectedNCR.cost
                  ? `₱${Number(selectedNCR.cost_impact || selectedNCR.cost).toLocaleString("en-PH", {
                      minimumFractionDigits: 2,
                    })}`
                  : "—"}
              </div>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, marginBottom: 4 }}>
                PO Number
              </div>
              <div style={{ fontSize: 13 }}>{selectedNCR.po_no || "—"}</div>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, marginBottom: 4 }}>
                Description
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                {selectedNCR.description || "—"}
              </div>
            </div>

            {selectedNCR.corrective_action && (
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, marginBottom: 4 }}>
                  Corrective Action
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                  {selectedNCR.corrective_action}
                </div>
              </div>
            )}

            {selectedNCR.closed_date && (
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600, marginBottom: 4 }}>
                  Closed Date
                </div>
                <div style={{ fontSize: 13 }}>
                  {new Date(selectedNCR.closed_date).toLocaleDateString("en-PH", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Raise NCR Modal */}
      {showRaiseNCR && (
        <Modal
          title="Raise NCR"
          onClose={() => setShowRaiseNCR(false)}
          footer={
            <>
              <button className="btn" onClick={() => setShowRaiseNCR(false)}>
                Cancel
              </button>
              <button className="btn primary" onClick={handleRaiseNCR}>
                <Plus size={14} />
                Raise NCR
              </button>
            </>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                Vendor *
              </label>
              <input
                type="text"
                value={ncrForm.vendor_id}
                onChange={(e) => setNcrForm({ ...ncrForm, vendor_id: e.target.value })}
                placeholder="Enter vendor ID"
                style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                Product *
              </label>
              <input
                type="text"
                value={ncrForm.product_id}
                onChange={(e) => setNcrForm({ ...ncrForm, product_id: e.target.value })}
                placeholder="Enter product ID"
                style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                  PO Number
                </label>
                <input
                  type="text"
                  value={ncrForm.po_no}
                  onChange={(e) => setNcrForm({ ...ncrForm, po_no: e.target.value })}
                  placeholder="PO-XXXX"
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                  NCR Date
                </label>
                <input
                  type="date"
                  value={ncrForm.ncr_date}
                  onChange={(e) => setNcrForm({ ...ncrForm, ncr_date: e.target.value })}
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                  Type
                </label>
                <select
                  value={ncrForm.ncr_type}
                  onChange={(e) => setNcrForm({ ...ncrForm, ncr_type: e.target.value })}
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
                >
                  {NCR_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                  Severity
                </label>
                <select
                  value={ncrForm.severity}
                  onChange={(e) => setNcrForm({ ...ncrForm, severity: e.target.value })}
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
                >
                  {SEVERITY_OPTIONS.map((sev) => (
                    <option key={sev} value={sev}>
                      {sev}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                  Quantity Affected
                </label>
                <input
                  type="number"
                  value={ncrForm.qty_affected}
                  onChange={(e) => setNcrForm({ ...ncrForm, qty_affected: Number(e.target.value) })}
                  min={0}
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                  Cost Impact (₱)
                </label>
                <input
                  type="number"
                  value={ncrForm.cost_impact}
                  onChange={(e) => setNcrForm({ ...ncrForm, cost_impact: Number(e.target.value) })}
                  min={0}
                  step={0.01}
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                Status
              </label>
              <select
                value={ncrForm.status}
                onChange={(e) => setNcrForm({ ...ncrForm, status: e.target.value })}
                style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {ncrForm.status === "Closed" && (
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                  Closed Date
                </label>
                <input
                  type="date"
                  value={ncrForm.closed_date}
                  onChange={(e) => setNcrForm({ ...ncrForm, closed_date: e.target.value })}
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13 }}
                />
              </div>
            )}

            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                Description *
              </label>
              <textarea
                value={ncrForm.description}
                onChange={(e) => setNcrForm({ ...ncrForm, description: e.target.value })}
                rows={3}
                placeholder="Describe the non-conformance issue..."
                style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13, resize: "vertical" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
                Corrective Action
              </label>
              <textarea
                value={ncrForm.corrective_action}
                onChange={(e) => setNcrForm({ ...ncrForm, corrective_action: e.target.value })}
                rows={2}
                placeholder="Describe corrective actions..."
                style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", background: "var(--panel)", color: "var(--ink)", fontSize: 13, resize: "vertical" }}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
